import { Component, Inject, OnInit } from '@angular/core';
import { ValidationErrors } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { AlertsType } from '@rappi/ui/alerts';

import { AlertsService } from '../../../../services/alerts/alerts.service';
import { MakersFinancesMgrService } from '../../../services/makers-finances-mgr.service';
import { PHONE_FIELD_ERROR, PHONE_FIELD_MESSAGE } from '../../phone-field/phone-field.constants';
import { SelectOption } from '../../purchase-order-detail/sections/section-base.types';
import { FH_COMPANY_LABEL } from '../../company-form/company-form.constants';

export const CONTACT_FORM_DIALOG_LABEL = {
  CREATE_TITLE: 'Create contact',
  EDIT_TITLE:   'Edit contact',
  NAME:         'Name',
  EMAIL:        'Email',
  PHONE:        'Phone',
  JOB_TITLE:    'Job title',
  CREATE:       'Create contact',
  SAVE:         'Save changes',
  CREATED:      'Contact created.',
  SAVED:        'Contact updated.',
  LOAD_FAILED:  'Could not load the contact. Please try again.',
  CREATE_FAILED: 'Could not create the contact. Please try again.',
  SAVE_FAILED:  'Could not update the contact. Please try again.',
  REQUIRED:     '*Required',
  EMAIL_ERROR:  'Enter a valid email address.',
} as const;

export interface ContactFormDialogData {
  /** Present in edit mode — opens the dialog pre-populated with the contact's current data. */
  contactId?: string;
  /** Present in create mode only. Unused by the create endpoint but kept for signature compat. */
  companyId?: string;
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

@Component({
  selector: 'app-contact-form-dialog',
  templateUrl: './contact-form-dialog.component.html',
  styleUrls: ['./contact-form-dialog.component.scss'],
})
export class ContactFormDialogComponent implements OnInit {
  readonly LABELS = CONTACT_FORM_DIALOG_LABEL;
  readonly COMMON = FH_COMPANY_LABEL;
  readonly PHONE_ERROR = PHONE_FIELD_ERROR;
  readonly PHONE_MESSAGE = PHONE_FIELD_MESSAGE;

  private readonly _contactId: number | null = null;

  name = '';
  email = '';
  phone = '';
  jobTitle = '';
  submitted = false;
  isSaving = false;
  isLoading = false;
  loadFailed = false;

  constructor(
    public readonly dialogRef: MatDialogRef<ContactFormDialogComponent, SelectOption | null>,
    private readonly financesService: MakersFinancesMgrService,
    private readonly alerts: AlertsService,
    @Inject(MAT_DIALOG_DATA) public readonly data: ContactFormDialogData,
  ) {
    if (data.contactId) {
      this._contactId = Number(data.contactId);
    }
  }

  get isEditMode(): boolean {
    return this._contactId !== null;
  }

  get title(): string {
    return this.isEditMode ? this.LABELS.EDIT_TITLE : this.LABELS.CREATE_TITLE;
  }

  get confirmLabel(): string {
    return this.isEditMode ? this.LABELS.SAVE : this.LABELS.CREATE;
  }

  ngOnInit(): void {
    const id = this._contactId;
    if (id === null) {
      return;
    }
    this.isLoading = true;
    this.financesService.getContact(id).subscribe({
      next: res => {
        this.isLoading = false;
        this.name = res.data.full_name ?? '';
        this.email = res.data.email ?? '';
        this.phone = res.data.phone ?? '';
        this.jobTitle = res.data.job_title ?? '';
      },
      error: () => {
        this.isLoading = false;
        this.loadFailed = true;
        this.alerts.openAlerts(this.LABELS.LOAD_FAILED, AlertsType.error);
      },
    });
  }

  get hasNameError(): boolean {
    return this.submitted && !this.name.trim();
  }

  get hasEmailError(): boolean {
    return this.submitted && !EMAIL_PATTERN.test(this.email.trim());
  }

  phoneErrorMessage(errors: ValidationErrors | null): string | null {
    if (!errors || !errors[this.PHONE_ERROR.LENGTH]) {
      return null;
    }
    return this.PHONE_MESSAGE[this.PHONE_ERROR.LENGTH];
  }

  cancel(): void {
    this.dialogRef.close(null);
  }

  confirm(): void {
    this.submitted = true;
    if (this.hasNameError || this.hasEmailError || this.isSaving || this.loadFailed) {
      return;
    }

    this.isSaving = true;

    const id = this._contactId;
    if (id !== null) {
      this.financesService.updateContact(id, {
        email: this.email.trim(),
        full_name: this.name.trim() || undefined,
        phone: this.phone.trim() || undefined,
        job_title: this.jobTitle.trim() || undefined,
      }).subscribe({
        next: res => {
          this.isSaving = false;
          this.alerts.openAlerts(this.LABELS.SAVED, AlertsType.success);
          this.dialogRef.close({
            id: String(res.data.id),
            name: `${res.data.full_name}  ·  ${res.data.email}`,
          });
        },
        error: () => {
          this.isSaving = false;
          this.alerts.openAlerts(this.LABELS.SAVE_FAILED, AlertsType.error);
        },
      });
      return;
    }

    this.financesService.createContact({
      full_name: this.name.trim(),
      email: this.email.trim(),
      phone: this.phone.trim() || undefined,
      job_title: this.jobTitle.trim() || undefined,
    }).subscribe({
      next: res => {
        this.isSaving = false;
        this.alerts.openAlerts(this.LABELS.CREATED, AlertsType.success);
        this.dialogRef.close({
          id: String(res.data.id),
          name: `${res.data.full_name}  ·  ${res.data.email}`,
        });
      },
      error: () => {
        this.isSaving = false;
        this.alerts.openAlerts(this.LABELS.CREATE_FAILED, AlertsType.error);
      },
    });
  }
}
